import { useRef } from "react";
import type { CSSProperties } from "react";
import { HIGH, LOW, useSettings, type SliderKey } from "@/os/state/settings";
import { useTheme, type Theme } from "@/os/state/theme";
import { useToast } from "@/os/state/toast";
import { play } from "@/lib/sound";

const META: { key: SliderKey; label: string; color: string; desc: (v: number) => string }[] = [
    {
        key: "curiosity",
        label: "Curiosity",
        color: "#2563EB",
        desc: (v) =>
            v >= HIGH
                ? "extra files, secret tabs and side doors unlocked."
                : v >= 35
                  ? "open tabs: manageable. barely."
                  : "curious, responsibly.",
    },
    {
        key: "overthinking",
        label: "Overthinking",
        color: "#A855F7",
        desc: (v) =>
            v >= HIGH
                ? "sticky notes and a decision simulator deployed."
                : v >= 35
                  ? "mild second-guessing. healthy-ish."
                  : "decisions made once. wild.",
    },
    {
        key: "sleep",
        label: "Sleep Optimization",
        color: "#8B5CF6",
        desc: (v) =>
            v >= HIGH ? "the OS is yawning — theme softened, motion calmed." : v >= 35 ? "mostly-rested runtime." : "late-night build energy.",
    },
    {
        key: "tolerance",
        label: "Tolerance for Slow Apps",
        color: "#FF6B6B",
        desc: (v) => (v <= LOW ? "simulated loading screens incoming." : v >= 70 ? "patient. suspiciously patient." : "mild toe-tapping permitted."),
    },
];

const PRESETS: { name: string; values: Record<SliderKey, number> }[] = [
    { name: "chaotic research", values: { curiosity: 100, overthinking: 90, sleep: 10, tolerance: 0 } },
    { name: "bedtime conflict", values: { curiosity: 80, overthinking: 40, sleep: 95, tolerance: 50 } },
    { name: "performance impatience", values: { curiosity: 60, overthinking: 80, sleep: 40, tolerance: 10 } },
];

export function PersonalitySettings() {
    const { sliders, setSlider, reset } = useSettings();
    const { theme, setTheme } = useTheme();
    const { push } = useToast();
    const pokes = useRef(0);

    const poke = () => {
        pokes.current += 1;
        if (pokes.current === 5) {
            push("system", "curiosity is already at maximum.", "i respect the ambition. this is not an easter egg. (it's not NOT one.)");
        }
    };

    return (
        <div data-testid="personality-settings">
            <p className="font-mono text-[11px] leading-relaxed text-inkfaint">
                humorous interface controls, not psychological measurements. changes apply across the OS immediately. thresholds:
                high ≥ {HIGH}, low ≤ {LOW}.
            </p>
            <div className="mt-4 space-y-5">
                {META.map((m) => (
                    <div key={m.key}>
                        <div className="flex items-center justify-between gap-2">
                            <button
                                className="font-display text-sm font-extrabold text-ink"
                                data-testid={`slider-label-${m.key}`}
                                onClick={m.key === "curiosity" ? poke : undefined}
                            >
                                {m.label}
                            </button>
                            <span className="badge" style={{ borderColor: m.color, color: m.color }} data-testid={`slider-value-${m.key}`}>
                                {sliders[m.key]}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={sliders[m.key]}
                            data-testid={`slider-${m.key}`}
                            aria-label={m.label}
                            aria-valuenow={sliders[m.key]}
                            className="os-slider mt-2"
                            style={{ "--fill": `${sliders[m.key]}%`, "--fill-color": m.color } as CSSProperties}
                            onChange={(e) => setSlider(m.key, Number(e.target.value))}
                        />
                        <p className="mt-1 font-mono text-[10.5px] text-inkfaint" data-testid={`slider-desc-${m.key}`}>
                            {m.desc(sliders[m.key])}
                        </p>
                    </div>
                ))}
            </div>
            <div className="mt-6">
                <p className="font-mono text-[11px] font-bold">wallpaper</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {([
                        { id: "cream" as Theme, label: "cream", from: "#FFFDF7", to: "#EAE5D9" },
                        { id: "butter" as Theme, label: "butter", from: "#FFFBEB", to: "#F1E3A8" },
                        { id: "lavender" as Theme, label: "lavender", from: "#FBF7FF", to: "#E0D0F2" },
                    ]).map((w) => (
                        <button
                            key={w.id}
                            data-testid={`theme-swatch-${w.id}`}
                            aria-label={`Wallpaper: ${w.label}`}
                            aria-pressed={theme === w.id}
                            className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 font-mono text-[11px] transition-transform hover:-translate-y-0.5 ${
                                theme === w.id ? "border-elec" : "border-ink"
                            }`}
                            onClick={() => {
                                setTheme(w.id);
                                play("click");
                            }}
                        >
                            <span
                                className="h-5 w-5 rounded-md border border-ink"
                                style={{ background: `radial-gradient(circle at 50% 30%, ${w.from}, ${w.to})` }}
                            />
                            {w.label}
                            {theme === w.id ? " ✓" : ""}
                        </button>
                    ))}
                </div>
                <p className="mt-1.5 font-mono text-[10px] text-inkfaint">
                    (or whisper "theme lavender" to terminal.exe — but you no longer have to.)
                </p>
            </div>
            <div className="mt-6">
                <p className="font-mono text-[11px] font-bold">presets (a responsible person's shortcuts)</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                        <button
                            key={p.name}
                            data-testid={`preset-${p.name.replace(/ /g, "-")}`}
                            className="chip hover:bg-sandsoft"
                            onClick={() => {
                                (Object.keys(p.values) as SliderKey[]).forEach((k) => setSlider(k, p.values[k]));
                            }}
                        >
                            {p.name}
                        </button>
                    ))}
                </div>
                <button data-testid="settings-reset" className="btn btn-sm mt-4" onClick={reset}>
                    Reset to defaults
                </button>
            </div>
        </div>
    );
}
